// ─── English translations for Adventure Track! ───
const en = {
  // ── Common / Shared ──
  common: {
    loading: "Loading...",
    save: "Save",
    saving: "Saving...",
    cancel: "Cancel",
    back: "Back",
    viewAll: "View all",
    seeMore: "See more",
    refresh: "Refresh",
    search: "Search",
    delete: "Delete",
    edit: "Edit",
    remove: "Remove",
    create: "Create",
    close: "Close",
    authenticating: "Authenticating...",
    browsMedia: "Browse content",
  },

  // ── Media types ──
  mediaTypes: {
    book: "Book",
    movie: "Movie",
    tv_series: "TV Series",
    video_game: "Video Game",
    manga: "Manga",
    comic: "Comic",
    graphic_novel: "Graphic Novel",
  },
  mediaTypesPlural: {
    book: "Books",
    movie: "Movies",
    tv_series: "TV Series",
    video_game: "Video Games",
    manga: "Manga",
    comic: "Comics",
    graphic_novel: "Graphic Novels",
  },
  mediaTypesShort: {
    book: "Book",
    movie: "Movie",
    tv_series: "TV",
    video_game: "Game",
    manga: "Manga",
    comic: "Comic",
    graphic_novel: "GN",
  },

  // ── Statuses ──
  statuses: {
    planned: "Planned",
    in_progress: "In Progress",
    completed: "Completed",
    dropped: "Dropped",
  },
  statusesShort: {
    planned: "Planned",
    in_progress: "Active",
    completed: "Done",
    dropped: "Dropped",
  },

  // ── Navigation ──
  nav: {
    dashboard: "Your Adventure",
    library: "Library",
    search: "Search",
    whatsNext: "What's Next?",
    achievements: "Achievements",
    challenges: "Challenges",
    profile: "Profile",
    home: "Home",
    discover: "Activity",
    lightMode: "Light mode",
    darkMode: "Dark mode",
    logout: "Log out",
    loggedOut: "Logged out successfully",
  },

  // ── Landing page ──
  landing: {
    signInGoogle: "Sign in with Google",
    signUpGoogle: "Sign up with Google",
    heroTitle1: "Discover.",
    heroTitle2: "Connect.",
    heroTitle3: "Share.",
    heroSubtitle1: "All your content, in one place.",
    heroSubtitle2: "And all the stories, connected.",
    startAdventure: "Start your adventure",
    trackAllMedia: "Everything connected in one place",
    readyToStart: "It all starts with a story",
    joinToday: "Join Adventure Track! An adventure is more fun when shared.",
    trackJourney: "Organize your multimedia journey.",
    featuresCommon: "Track, organize, and follow all your content without switching platforms",
    features: {
      books: { title: "Books" },
      movies: { title: "Movies" },
      tvSeries: { title: "TV Series" },
      videoGames: { title: "Video Games" },
    },
    stats: {
      rate: { value: "Your rating matters", label: "Rate every story" },
      track: { value: "Every step counts", label: "Unlock new achievements" },
      review: { value: "Leave your mark", label: "Share your thoughts" },
    },
  },

  // ── Dashboard ──
  dashboard: {
    welcomeBack: "Hello,",
    overview: "Everything that's part of your adventure",
    totalItems: "Total",
    inProgress: "In Progress",
    completed: "Completed",
    avgRating: "Average Rating",
    quickActions: "Quick Actions",
    discover: "Discover",
    whatsNext: "What's Next?",
    activeGoals: "Active Goals",
    completionRate: "Completion Rate",
    itemsCompleted: (done, total) => `${done} of ${total} completed`,
    byMediaType: "By media type",
    currentlyInProgress: "In Progress",
    recentlyAdded: "Recently Added",
    viewLibrary: "View Library",
    emptyTitle: "Your library is empty",
    emptyDescription: "Start tracking your content by searching and adding titles",
    loadError: "Error loading dashboard",
    loadingDashboard: "Loading dashboard...",
    planned: "Planned",
    dropped: "Dropped",
    exploreSection: "Explore",
    connectSection: "Connect",
    latestActivity: "Latest activity",
    achievementProgress: "Your achievement progress",
    youAreAt: (stage) => `You're at ${stage}`,
    achievementOnTrack: (n) => `You're on track! ${n} achievement${n !== 1 ? "s" : ""} remaining to complete this stage.`,
    achievementAllDone: "Congratulations! You've completed all achievements in this stage.",
    collectionByType: "Your collection by type",
  },

  // ── Library ──
  library: {
    title: "My Library",
    itemCount: (n) => `${n} ${n === 1 ? "title" : "titles"} in your collection`,
    addItem: "Add title",
    gridView: "Grid view",
    listView: "List view",
    allTypes: "All",
    allStatuses: "All statuses",
    allRatings: "All ratings",
    unrated: "Unrated",
    recentlyAdded: "Recently Added",
    recentlyUpdated: "Recently Updated",
    rating: "Rating",
    clearFilters: "Clear filters",
    moreFilters: "More filters",
    sortByPriority: "Priority",
    priorityLabel: "Priority",
    dragHint: "Drag to reorder",
    addTitle: "Add title",
    contentType: "Content type",
    loadingLibrary: "Loading library...",
    noMatch: "No titles match the filters",
    emptyLibrary: "Your library is empty",
    adjustFilters: "Try adjusting the filters",
    startSearching: "Start searching and adding content",
    loadError: "Error loading library",
  },

  // ── Search ──
  search: {
    title: "Search",
    subtitle: "Find your next adventure",
    placeholder: "Search titles...",
    searchButton: "Search",
    searching: "Searching...",
    resultsFor: (n, q) => `${n} results for "${q}"`,
    noResults: "No results",
    noResultsFor: (q) => `No results found for "${q}". Try another search.`,
    createNewTitle: "Create new title",
    trendingThisWeek: "Trending This Week",
    loadingTrending: "Loading trending...",
    noTrending: "No trending content available.",
    searchFailed: "Search failed",
    allTypes: "All types",
    popular: "Popular",
  },

  // ── Profile ──
  profile: {
    name: "Name",
    bio: "Bio",
    bioPlaceholder: "Tell us about yourself...",
    anonymous: "Anonymous",
    profileUpdated: "Profile updated",
    updateError: "Error updating profile",
    total: "Total",
    done: "Done",
    active: "Active",
    rating: "Rating",
    collectionBreakdown: "Collection Breakdown",
    myCollection: "My Collection",
    all: "All",
    noItemsYet: "No titles in your library yet",
    noStatusItems: (s) => `No ${s} titles`,
    viewAllItems: (n) => `View all (${n} titles)`,
    loadingProfile: "Loading profile...",
    avatarUpdated: "Profile picture updated",
    avatarError: "Error uploading image",
    themeLight: "Light",
    themeDark: "Dark",
    themeSystem: "System",
    themeLabel: "App theme",
    preferences: "Preferences",
  },

  // ── Achievements ──
  achievements: {
    title: "Achievements",
    subtitle: "Track your progress and unlock badges",
    yourProgress: "Your progress",
    unlocked: "Achievements unlocked",
    moreToUnlock: (n) => `${n} more to unlock!`,
    unlockedSection: (n) => `Unlocked (${n})`,
    lockedSection: (n) => `Locked (${n})`,
    noAchievements: "No achievements available",
    startAdding: "Add titles to your library to unlock achievements!",
    earnedOn: (date) => `Earned on ${date}`,
    overallProgress: "Overall progress",
    achievementsUnlocked: "achievements unlocked",
    yourStreak: "Your streak",
    consecutiveDays: "consecutive days",
    totalDays: "total days",
    stageCompleted: "Stage completed",
    remainingAchievements: (n) => `${n} achievement${n !== 1 ? "s" : ""} remaining to complete this stage.`,
    hiddenAchievements: "Hidden achievements that are revealed upon completion.",
    shareOnInstagram: "Share on Instagram",
  },

  // ── Challenges ──
  challenges: {
    title: "Monthly Challenges",
    subtitle: "Set goals and track your progress",
    newChallenge: "New challenge",
    createTitle: "Create new challenge",
    createDescription: "Set a monthly goal to complete a certain number of titles.",
    challengeTitle: "Challenge name",
    challengePlaceholder: "E.g. Read 5 books this month",
    targetCount: "Target count",
    mediaTypeOptional: "Media type (optional)",
    allTypes: "All types",
    month: "Month",
    year: "Year",
    createChallenge: "Create challenge",
    creating: "Creating...",
    progress: "Progress",
    challengeCompleted: "Challenge completed!",
    moreToGo: (n) => `${n} more to go!`,
    activeChallenges: (n) => `Active challenges (${n})`,
    noActive: "No active challenges. Create one to get started!",
    completedChallenges: (n) => `Completed challenges (${n})`,
    created: "Challenge created",
    deleted: "Challenge deleted",
    titleRequired: "Enter a name for the challenge",
    createError: "Error creating challenge",
    deleteError: "Error deleting challenge",
    months: [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ],
  },

  // ── What's Next (Recommendations) ──
  whatsNext: {
    title: "What's Next?",
    subtitle: "Personalized recommendations based on your library",
    startBuilding: "Start building your library",
    startBuildingDesc: "Add titles to your library and we'll give you personalized recommendations!",
    basedOnFavorites: "Based on your favorites",
    popularIn: (type) => `Popular in ${type}`,
    trendingNow: "Trending now",
    trendingThisWeek: "Trending this week",
    fromCreators: "From creators you like",
    byCreator: (name) => `By ${name}`,
    highlyRated: "Highly rated",
    ratingLabel: (r) => `★ ${r} rating`,
    loadError: "Error loading recommendations",
  },

  // ── Media Detail ──
  mediaDetail: {
    editEntry: "Edit entry",
    removeFromLibrary: "Remove",
    addToLibrary: "Add to Library",
    votes: (n) => `(${n.toLocaleString()} votes)`,
    season: "Season",
    seasons: "Seasons",
    episodes: "Episodes",
    overview: "Overview",
    director: "Director",
    creator: "Creator",
    cast: "Cast",
    yourNotes: "Your notes",
    review: "Review",
    notes: "Notes",
    communityReviews: "Community reviews",
    notFound: "Title not found",
    loadError: "Error loading title",
    removed: "Removed from library",
    removeError: "Error removing",
    yourRating: "Your rating:",
    description: "Description",
    yourEntry: "Your entry",
    ratingCount: (n) => `(${n} ${n === 1 ? "rating" : "ratings"})`,
    removedFromLibrary: "Removed from library",
    removeFromLibraryError: "Error removing from library",
    share: "Share",
    reviews: "Reviews",
    communityStatus: "How the community is experiencing it",
    communityTabCompleted: "Completed",
    communityTabInProgress: "Currently watching",
    communityTabPlanned: "On their radar",
    communityEmpty: "No users in this category yet.",
  },

  // ── Add to Library Dialog ──
  addToLibraryDialog: {
    editLibrary: "Edit in library",
    addToLibrary: "Add to Library",
    status: "Status",
    rating: "Rating",
    reviewOptional: "Review (optional)",
    reviewPlaceholder: "Share your thoughts...",
    notesOptional: "Personal notes (optional)",
    notesPlaceholder: "Private notes for yourself...",
    update: "Update",
    addButton: "Add to Library",
    updated: "Entry updated",
    added: "Added to library",
    saveError: "Error saving",
  },

  // ── Create Item Dialog ──
  createItem: {
    title: "Add new title",
    subtitle: "Manually add a title to the database",
    titleLabel: "Title *",
    titlePlaceholder: "Enter the title...",
    mediaType: "Media type",
    releaseYear: "Release year",
    creator: "Creator / Author / Developer",
    creatorPlaceholder: "E.g. CD Projekt Red, Eiichiro Oda...",
    coverImage: "Cover URL (optional)",
    description: "Description (optional)",
    descriptionPlaceholder: "Brief description of the title...",
    createButton: "Create title",
    creating: "Creating...",
    titleRequired: "Title is required",
    createdSuccess: (title) => `"${title}" created successfully`,
    createError: "Error creating title",
  },

  // ── TMDB Result Card ──
  tmdb: {
    inDatabase: "In database",
    saveToDb: "Save",
    savedToDb: (title) => `"${title}" saved to database`,
    saveError: "Error saving title",
  },

  // ── Social / Profiles ──
  social: {
    followers: "Followers",
    following: "Following",
    follow: "Follow",
    unfollow: "Unfollow",
    followersOf: (name) => `${name}'s followers`,
    followingOf: (name) => `${name} is following`,
    noFollowers: "No followers yet",
    noFollowing: "Not following anyone yet",
    loadMore: "Load more",
    userNotFound: "User not found",
    username: "Username",
    usernamePlaceholder: "your_username",
    usernameUpdated: "Username updated",
    usernameError: "Error updating username",
    viewProfile: "View profile",
    editProfile: "Edit profile",
    memberSince: "Member since",
    publicLibrary: "Public library",
  },

  // ── Activity Feed ──
  feed: {
    title: "Activity",
    subtitle: "Latest from people you follow",
    empty: "No activity yet",
    emptyDesc: "Follow other users to see their activity here",
    findPeople: "Find people",
    tabFeed: "Friends' adventures",
    tabRecommendations: "Recommended adventures",
    recommendedYou: "recommended you",
    aTitle: "a title",
    noRecommendations: "No recommendations",
    noRecommendationsDesc: "When someone recommends something, it will appear here.",
    actions: {
      "quiere ver": "wants to watch",
      "está viendo": "is watching",
      "completó": "completed",
      "abandonó": "dropped",
      "calificó": "rated",
      "agregó": "added",
      "actualizó": "updated",
    },
    ratedWith: (n) => `with ${n}/5`,
    timeAgo: {
      now: "now",
      minutes: (n) => `${n}m ago`,
      hours: (n) => `${n}h ago`,
      days: (n) => `${n}d ago`,
    },
  },

  // ── User Search ──
  userSearch: {
    title: "Find people",
    placeholder: "Search by name or username...",
    noResults: "No users found",
    searching: "Searching...",
  },

  // ── Notifications ──
  notifications: {
    title: "Notifications",
    empty: "You have no notifications",
    markAllRead: "Mark all as read",
    newFollower: "started following you",
  },

  // ── Share Modal ──
  share: {
    title: "Share",
    copyLink: "Copy link",
    linkCopied: "Link copied",
    createImage: "Create image to share",
    recommendFriend: "Recommend to a friend",
    searchUser: "Search user...",
    noUsersFound: "No users found",
    notFollowingAnyone: "You don't follow anyone yet",
    recommendationSent: "Recommendation sent",
    imageTitle: "Image to share",
    downloadImage: "Download image",
    downloadHelper: "Download the image and share it on Instagram",
    imageForShare: "Image to share",
  },

  // ── Import Modal ──
  importModal: {
    title: "Import your library",
    description: "Upload a file exported from other platforms (Goodreads, Letterboxd, IMDb, Backloggd, Trakt)",
    dragHere: "Drag your file here",
    clickToSelect: "or click to select",
    importButton: "Import library",
    importing: "Importing...",
    errorTitle: "Error importing",
    tryAgain: "Try again",
    imported: (n) => `You imported ${n} title${n !== 1 ? "s" : ""}`,
    fromPlatform: "from",
    importedCount: "Imported",
    duplicates: "Duplicates",
    failed: "Failed",
    achievementsUnlocked: (n) => `You unlocked ${n} new achievement${n !== 1 ? "s" : ""}`,
    goToAchievements: "Go to Achievements to see them",
    done: "Done",
  },

  // ── Social Share Card ──
  shareCard: {
    started: (title) => `Started ${title}`,
    completed: (title) => `Finished ${title}`,
    planned: (title) => `Added to my radar ${title}`,
    achievement: "Unlocked an achievement",
  },

  // ── Achievements (extra keys) ──
  achievementsExtra: {
    overallProgress: "Overall progress",
    achievementsUnlocked: "achievements unlocked",
    yourStreak: "Your streak",
    consecutiveDays: "consecutive days",
    totalDays: "total days",
    stageCompleted: "Stage completed",
    remainingAchievements: (n) => `${n} achievement${n !== 1 ? "s" : ""} remaining to complete this stage.`,
    hiddenAchievements: "Hidden achievements that are revealed upon completion.",
    shareOnInstagram: "Share on Instagram",
  },

  // ── Media Detail (extra keys) ──
  mediaDetailExtra: {
    share: "Share",
    reviews: "Reviews",
    communityStatus: "How the community is experiencing it",
    communityTabCompleted: "Completed",
    communityTabInProgress: "Currently watching",
    communityTabPlanned: "On their radar",
    communityEmpty: "No users in this category yet.",
  },

  // ── Achievement stage name translations ──
  achievementStages: {
    "Inicio": "Beginning",
    "Exploración": "Exploration",
    "Colección": "Collector",
    "Comunidad": "Community",
    "Constancia": "Constancy",
    "Maestría": "Mastery",
    "Logros Secretos": "Easter Eggs",
  },

  // ── Language ──
  language: {
    label: "Language",
    es: "Español",
    en: "English",
  },
};

export default en;
